import api from "../axios";

/**
 * Get all HSE documents with optional filters
 * @param {Object} filters - Filter options
 * @param {number} filters.discipline_id - Filter by discipline ID
 * @param {string} filters.category - Filter by category code
 * @param {string} filters.chapter - Filter by chapter number
 * @param {string} filters.document_type - Filter by document type (policy, procedure, etc.)
 * @param {string} filters.status - Filter by status
 * @param {string} filters.search - Search query
 */
export const getHseDocuments = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.discipline_id) params.append("discipline_id", filters.discipline_id);
  if (filters.category) params.append("category", filters.category);
  if (filters.chapter) params.append("chapter", filters.chapter);
  if (filters.document_type) params.append("document_type", filters.document_type);
  if (filters.status) params.append("status", filters.status);
  if (filters.search) params.append("search", filters.search);
  if (filters.include_fillable_forms) params.append("include_fillable_forms", "true");
  
  const response = await api.get(`/hse_documents?${params.toString()}`);
  return response.data;
};

/**
 * Get a specific HSE document by ID
 * @param {number} id - Document ID
 * @param {boolean} forEdit - If true, returns draft version content if available
 */
export const getHseDocument = async (id, forEdit = false) => {
  const params = forEdit ? '?for_edit=true' : '';
  const response = await api.get(`/hse_documents/${id}${params}`);
  return response.data;
};

/**
 * Create a new HSE document
 * @param {Object} documentData - Document data
 */
export const createHseDocument = async (documentData) => {
  // Separate access params from document params
  const { access_type, user_ids, ...docParams } = documentData;
  const payload = { document: docParams };
  
  // Add access params at top level if provided
  if (access_type) {
    payload.access_type = access_type;
    if (user_ids) {
      payload.user_ids = user_ids;
    }
  }
  
  const response = await api.post("/hse_documents", payload);
  return response.data;
};

/**
 * Bulk-import HSE documents (+ optional new discipline/category/chapter structure)
 * @param {Object} payload - { create_missing_structure, structure, documents }
 */
export const bulkImportHseDocuments = async (payload) => {
  const response = await api.post("/hse_documents/bulk_import", { payload });
  return response.data;
};

/**
 * Update an existing HSE document
 * @param {number} id - Document ID
 * @param {Object} documentData - Updated document data
 * @param {Object} additionalParams - Additional top-level parameters (e.g., create_revision, change_summary)
 */
export const updateHseDocument = async (id, documentData, additionalParams = {}) => {
  const payload = {
    document: documentData,
    ...additionalParams
  };
  const response = await api.patch(`/hse_documents/${id}`, payload);
  return response.data;
};

/**
 * Delete an HSE document
 * @param {number} id - Document ID
 */
export const deleteHseDocument = async (id) => {
  const response = await api.delete(`/hse_documents/${id}`);
  return response.data;
};

/**
 * Get access information for a document (admin only)
 * @param {number} id - Document ID
 */
export const getDocumentAccess = async (id) => {
  const response = await api.get(`/hse_documents/${id}/access`);
  return response.data;
};

/**
 * Update access for a document (admin only)
 * @param {number} id - Document ID
 * @param {Object} accessData - Access data
 * @param {string} accessData.access_type - 'all' or 'specific'
 * @param {Array<number>} accessData.user_ids - Array of user IDs (if specific)
 * @param {boolean} accessData.can_read - Can read permission
 * @param {boolean} accessData.can_approve - Can approve permission
 */
export const updateDocumentAccess = async (id, accessData) => {
  const response = await api.put(`/hse_documents/${id}/access`, accessData);
  return response.data;
};

/**
 * Submit document for approval
 * @param {number} id - Document ID
 * @param {string} changeSummary - Optional change summary
 */
export const submitDocumentForApproval = async (id, changeSummary = null) => {
  const response = await api.post(`/hse_documents/${id}/submit_for_approval`, { change_summary: changeSummary });
  return response.data;
};

/**
 * Print a single document
 * @param {number} id - Document ID
 * @param {number} versionId - Optional version ID (defaults to approved version)
 * @param {string} printType - Print type
 */
export const printDocument = async (id, versionId = null, printType = 'document') => {
  const params = new URLSearchParams({ printType });
  if (versionId) {
    params.append('version_id', versionId);
  }
  const response = await api.get(`/hse_documents/${id}/print_pdf?${params.toString()}`);
  return response.data;
};

/**
 * Print multiple documents as a book
 * @param {Array<number>} documentIds - Array of document IDs
 * @param {string} printType - Print type
 */
export const printMultipleDocuments = async (documentIds, printType = 'book') => {
  const response = await api.post('/hse_documents/print_multiple', {
    document_ids: documentIds,
    printType
  });
  return response.data;
};

/**
 * Approve a document version
 * @param {number} id - Document ID
 * @param {number} versionId - Version ID
 * @param {string} comment - Optional comment
 */
export const approveDocumentVersion = async (id, versionId, comment = null, options = {}) => {
  const payload = {
    comment,
    action_type: 'approve'
  };
  // false = minor revision: earlier «lest og forstått» is carried forward (no new read wave).
  if (typeof options.requiresReacknowledgement === "boolean") {
    payload.requires_reacknowledgement = options.requiresReacknowledgement;
  }
  const response = await api.post(`/hse_documents/${id}/versions/${versionId}/approve`, payload);
  return response.data;
};

/**
 * Reject a document version
 * @param {number} id - Document ID
 * @param {number} versionId - Version ID
 * @param {string} comment - Optional comment
 */
export const rejectDocumentVersion = async (id, versionId, comment = null) => {
  const response = await api.post(`/hse_documents/${id}/versions/${versionId}/approve`, { 
    comment,
    action_type: 'reject'
  });
  return response.data;
};

/**
 * Get all versions of a document
 * @param {number} id - Document ID
 */
export const getDocumentVersions = async (id) => {
  const response = await api.get(`/hse_documents/${id}/versions`);
  return response.data;
};

/**
 * Get approval status for a document version
 * @param {number} id - Document ID
 * @param {number} versionId - Version ID
 */
export const getVersionApprovals = async (id, versionId) => {
  const response = await api.get(`/hse_documents/${id}/versions/${versionId}/approvals`);
  return response.data;
};

/**
 * Confirm current user has read and understood an approved document version (completes linked read tasks).
 */
export const acknowledgeDocumentVersionRead = async (id, versionId, comment = null) => {
  const response = await api.post(`/hse_documents/${id}/versions/${versionId}/acknowledge_understood`, {
    comment,
  });
  return response.data;
};

/**
 * Admin / HMS document managers: company matrix of read/understood per document × employee.
 */
export const getHseDocumentReadAcknowledgements = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.document_id) params.append("document_id", filters.document_id);
  if (filters.user_id) params.append("user_id", filters.user_id);
  if (filters.status) params.append("status", filters.status);
  const query = params.toString();
  const response = await api.get(`/hse_documents/read_acknowledgements${query ? `?${query}` : ""}`);
  return response.data;
};

function mapReadOverviewPdfLocale(locale) {
  const value = String(locale || "nb").toLowerCase();
  if (value === "no" || value === "nb") return "nb";
  if (value === "sv" || value === "en" || value === "nn" || value === "pl") return value;
  if (value === "se") return "sv";
  return "nb";
}

/**
 * Download the admin read/understood overview as a PDF (grouped per user).
 * @param {Object} filters
 * @param {string} [filters.scope] pending | acknowledged | all
 * @param {number|string} [filters.document_id]
 * @param {number|string} [filters.user_id]
 * @param {string} [filters.locale]
 */
export const exportHseDocumentReadAcknowledgementsPdf = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.scope) params.append("scope", filters.scope);
  if (filters.document_id) params.append("document_id", filters.document_id);
  if (filters.user_id) params.append("user_id", filters.user_id);
  params.append("locale", mapReadOverviewPdfLocale(filters.locale));

  const query = params.toString();
  const response = await api.get(
    `/hse_documents/read_acknowledgements/export_pdf${query ? `?${query}` : ""}`,
    {
      responseType: "blob",
      headers: { Accept: "application/pdf" },
    }
  );

  const data = response.data;
  if (data?.type && data.type !== "application/pdf") {
    const text = await data.text();
    try {
      const json = JSON.parse(text);
      throw new Error(json.errors?.[0] || json.message || "Invalid PDF response");
    } catch (error) {
      if (error.message !== "Invalid PDF response") throw error;
      throw new Error("Response is not a PDF file");
    }
  }

  const arrayBuffer = await data.arrayBuffer();
  const uint8Array = new Uint8Array(arrayBuffer);
  const pdfHeader = String.fromCharCode(...uint8Array.slice(0, 4));
  if (pdfHeader !== "%PDF") {
    throw new Error("Invalid PDF file");
  }

  const blob = new Blob([arrayBuffer], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `lest_forstatt_${new Date().toISOString().split("T")[0]}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Create a checklist instance from an HSE document linked to a checklist type.
 * @param {number} id - HSE document ID
 * @param {Object} payload - { title?, customer_id?, project_id? }
 */
export const createChecklistFromHseDocument = async (id, payload = {}) => {
  const response = await api.post(`/hse_documents/${id}/create_checklist`, payload);
  return response.data;
};

/**
 * Re-capture checklist type snapshot on the open HSE document draft revision.
 * @param {number} id - HSE document ID
 */
export const refreshChecklistDraftSnapshot = async (id) => {
  const response = await api.post(`/hse_documents/${id}/refresh_checklist_snapshot`);
  return response.data;
};
